/**
 * Generador de PDFs de consentimientos informados.
 *
 * Dos templates:
 *  - generateMinorConsentPdf: Menor de edad (con padres + foto verificación)
 *  - generateAdultConsentPdf: Mayor de edad (firma propia)
 *
 * Salida: Blob (application/pdf) listo para subir a Storage.
 *
 * Notas:
 *  - jsPDF no maneja saltos automáticos de párrafos largos elegantemente, por eso
 *    usamos splitTextToSize antes de cada bloque.
 *  - Las firmas y fotos se incrustan como dataURL/JPEG.
 */

import { jsPDF } from "jspdf";
import type { ConsentFormSnapshot, ConsentParentRecord } from "@/types";

const PAGE_MARGIN = 15;
const LINE_HEIGHT = 5;
const MAX_WIDTH = 180; // letter width 210 - 2*15

interface PdfContext {
  pdf: jsPDF;
  y: number;
}

function ensureSpace(ctx: PdfContext, needed: number) {
  if (ctx.y + needed > 285) {
    ctx.pdf.addPage();
    ctx.y = PAGE_MARGIN;
  }
}

function writeTitle(ctx: PdfContext, text: string) {
  ensureSpace(ctx, 12);
  ctx.pdf.setFont("helvetica", "bold");
  ctx.pdf.setFontSize(14);
  ctx.pdf.text(text, PAGE_MARGIN, ctx.y);
  ctx.y += 8;
  ctx.pdf.setFont("helvetica", "normal");
  ctx.pdf.setFontSize(10);
}

function writeSection(ctx: PdfContext, text: string) {
  ensureSpace(ctx, 8);
  ctx.pdf.setFont("helvetica", "bold");
  ctx.pdf.setFontSize(11);
  ctx.pdf.text(text, PAGE_MARGIN, ctx.y);
  ctx.y += 6;
  ctx.pdf.setFont("helvetica", "normal");
  ctx.pdf.setFontSize(10);
}

function writeParagraph(ctx: PdfContext, text: string) {
  const lines = ctx.pdf.splitTextToSize(text, MAX_WIDTH);
  ensureSpace(ctx, lines.length * LINE_HEIGHT + 2);
  ctx.pdf.text(lines, PAGE_MARGIN, ctx.y);
  ctx.y += lines.length * LINE_HEIGHT + 2;
}

function writeKeyValue(ctx: PdfContext, key: string, value: string) {
  const text = `${key}: ${value}`;
  writeParagraph(ctx, text);
}

async function loadImageAsDataUrl(url: string): Promise<string | null> {
  try {
    if (!url || !url.trim()) return null;
    const res = await fetch(url);
    const contentType = (res.headers.get("content-type") || "").toLowerCase();
    if (!contentType.startsWith("image/")) {
      return null;
    }
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn("[pdf] failed to load image", url, err);
    return null;
  }
}

function getJsPdfImageFormat(dataUrl: string): "PNG" | "JPEG" | "WEBP" {
  const match = /^data:image\/([a-zA-Z0-9+.-]+);base64,/.exec(dataUrl);
  const mime = match?.[1]?.toLowerCase() ?? "png";
  if (mime === "jpeg" || mime === "jpg") return "JPEG";
  if (mime === "webp") return "WEBP";
  return "PNG";
}

function formatDateEs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-CO", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// =====================================================================
// MENOR DE EDAD
// =====================================================================
export async function generateMinorConsentPdf(args: {
  snapshot: ConsentFormSnapshot;
  parents: ConsentParentRecord[];
  participantSignatureDataUrl?: string; // opcional, firma del menor
}): Promise<Blob> {
  const { snapshot, parents } = args;
  const pdf = new jsPDF({ unit: "mm", format: "letter" });
  const ctx: PdfContext = { pdf, y: PAGE_MARGIN };

  writeTitle(
    ctx,
    "CONSENTIMIENTO INFORMADO Y AUTORIZACIONES PARA MENOR DE EDAD",
  );

  // 1. Identificación de los padres
  writeSection(ctx, "1. IDENTIFICACIÓN DE LOS PADRES O ACUDIENTES");
  parents.forEach((p, idx) => {
    writeParagraph(
      ctx,
      `${idx === 0 ? "Yo" : "Y yo"}, ${p.fullName}, mayor de edad, identificado(a) con ${p.idDocumentType} No. ${p.idDocumentNumber}, con domicilio en ${p.address}, en calidad de ${p.relationship}, AUTORIZO la participación del menor.`,
    );
  });

  writeKeyValue(ctx, "Nombre del menor", snapshot.memberFullName);
  writeKeyValue(ctx, "Tipo de documento", snapshot.memberIdDocumentType);
  writeKeyValue(ctx, "Número", snapshot.memberIdDocumentNumber);
  if (snapshot.memberHealthInsurance) {
    writeKeyValue(ctx, "EPS", snapshot.memberHealthInsurance);
  }

  // 2. Autorización de participación
  writeSection(ctx, "2. AUTORIZACIÓN DE PARTICIPACIÓN EN ACTIVIDAD");
  writeParagraph(
    ctx,
    "Manifestamos de manera libre, voluntaria e informada que autorizamos la participación de nuestro hijo(a) en la siguiente actividad institucional:",
  );
  writeKeyValue(ctx, "Actividad", snapshot.eventName);
  writeKeyValue(
    ctx,
    "Lugar",
    `${snapshot.eventLocationName} - ${snapshot.eventLocationCity}`,
  );
  writeKeyValue(
    ctx,
    "Fechas",
    `Del ${formatDateEs(snapshot.eventStartDate)} al ${formatDateEs(snapshot.eventEndDate)}`,
  );
  if (snapshot.departureCity)
    writeKeyValue(ctx, "Ciudad de salida", snapshot.departureCity);
  if (snapshot.returnCity)
    writeKeyValue(ctx, "Ciudad de regreso", snapshot.returnCity);
  writeKeyValue(
    ctx,
    "Club organizador",
    `Club de Conquistadores ${snapshot.clubName}`,
  );
  if (snapshot.churchName) {
    writeKeyValue(
      ctx,
      "Iglesia",
      `Iglesia Adventista del Séptimo Día ${snapshot.churchName}`,
    );
  }
  if (snapshot.districtName)
    writeKeyValue(ctx, "Distrito", snapshot.districtName);
  if (snapshot.boardVoteNumber && snapshot.boardVoteDate) {
    writeParagraph(
      ctx,
      `Esta actividad cuenta con el conocimiento y aprobación de la directiva del Club y de la Junta de Iglesia, según voto de junta No. ${snapshot.boardVoteNumber} del ${formatDateEs(snapshot.boardVoteDate)}.`,
    );
  }

  // 3. Información médica
  writeSection(ctx, "3. INFORMACIÓN MÉDICA DEL MENOR");
  if (snapshot.memberHealthInsurance) {
    writeKeyValue(ctx, "EPS", snapshot.memberHealthInsurance);
  }
  if (snapshot.memberNoMedicalConditions) {
    writeParagraph(
      ctx,
      "Declaramos bajo gravedad de juramento que el menor no presenta condiciones médicas relevantes conocidas.",
    );
  } else {
    if (snapshot.memberMedicalConditions) {
      writeKeyValue(
        ctx,
        "Condiciones médicas",
        snapshot.memberMedicalConditions,
      );
    }
    if (snapshot.memberAllergies) {
      writeKeyValue(ctx, "Alergias", snapshot.memberAllergies);
    }
    if (snapshot.memberMedications) {
      writeKeyValue(
        ctx,
        "Medicamentos permanentes",
        snapshot.memberMedications,
      );
    }
    if (
      !snapshot.memberMedicalConditions &&
      !snapshot.memberAllergies &&
      !snapshot.memberMedications
    ) {
      writeParagraph(
        ctx,
        "No se registró información médica relevante adicional.",
      );
    }
  }

  // 4. Atención médica
  writeSection(ctx, "4. AUTORIZACIÓN PARA ATENCIÓN MÉDICA DE URGENCIA");
  writeParagraph(
    ctx,
    "Autorizamos expresamente a los organizadores de la actividad, consejeros responsables y/o directores del club, para que en caso de urgencia o emergencia médica, nuestro hijo(a) reciba la atención médica inmediata que sea necesaria, incluyendo su traslado a un centro asistencial. La atención se realizará a través de la EPS indicada o del seguro correspondiente. Asumiremos los costos no cubiertos por el sistema de salud o póliza, si a ello hubiere lugar.",
  );

  // 5. Imagen
  writeSection(ctx, "5. AUTORIZACIÓN DE USO DE IMAGEN Y MATERIAL AUDIOVISUAL");
  writeParagraph(
    ctx,
    `Autorizo de manera previa, expresa e informada a la Unión Colombiana del Norte, ${snapshot.associationName} y al Club de Conquistadores ${snapshot.clubName} (IASD), para el uso del derecho de imagen del menor, incluyendo fotografías y producciones audiovisuales captadas durante el desarrollo de la actividad, con fines institucionales, educativos, informativos y pastorales, sin ánimo de lucro. Esta autorización incluye su uso en medios impresos, digitales y plataformas oficiales de la Iglesia Adventista del Séptimo Día, dentro y fuera del territorio nacional. Conozco que esta autorización puede ser revocada en los términos establecidos por la ley, mediante solicitud escrita.`,
  );

  // 6. Declaraciones finales
  writeSection(ctx, "6. DECLARACIONES FINALES");
  writeParagraph(
    ctx,
    "Declaro bajo gravedad de juramento que: (a) la información suministrada es veraz y completa; (b) actúo en calidad de representante legal del menor; (c) he leído y comprendido el contenido del presente documento y lo firmo de manera libre y voluntaria.",
  );

  // 7. Firmas + fotos verificación
  writeSection(ctx, "7. FIRMAS");
  for (let i = 0; i < parents.length; i++) {
    const p = parents[i];
    ensureSpace(ctx, 60);
    ctx.pdf.setFont("helvetica", "bold");
    ctx.pdf.text(
      `Padre/Madre/Acudiente ${i + 1} (${p.relationship}):`,
      PAGE_MARGIN,
      ctx.y,
    );
    ctx.y += 6;
    ctx.pdf.setFont("helvetica", "normal");

    const sigData = await loadImageAsDataUrl(p.signature.url);
    if (sigData) {
      try {
        ctx.pdf.addImage(
          sigData,
          getJsPdfImageFormat(sigData),
          PAGE_MARGIN,
          ctx.y,
          60,
          25,
        );
      } catch (e) {
        console.warn("addImage signature failed", e);
      }
    }
    const photoData = await loadImageAsDataUrl(p.verificationPhoto.url);
    if (photoData) {
      try {
        ctx.pdf.addImage(
          photoData,
          getJsPdfImageFormat(photoData),
          PAGE_MARGIN + 70,
          ctx.y,
          30,
          30,
        );
      } catch (e) {
        console.warn("addImage photo failed", e);
      }
    }
    ctx.y += 32;
    writeKeyValue(ctx, "Nombre", p.fullName);
    writeKeyValue(
      ctx,
      "Documento",
      `${p.idDocumentType} ${p.idDocumentNumber}`,
    );
    ctx.y += 4;
  }

  // Datos del menor
  writeKeyValue(ctx, "Nombre del menor", snapshot.memberFullName);
  writeKeyValue(
    ctx,
    "Documento",
    `${snapshot.memberIdDocumentType} ${snapshot.memberIdDocumentNumber}`,
  );
  writeKeyValue(ctx, "Fecha de firma", new Date().toLocaleDateString("es-CO"));

  return pdf.output("blob");
}

// =====================================================================
// ADULTO
// =====================================================================
export async function generateAdultConsentPdf(args: {
  snapshot: ConsentFormSnapshot;
  /** Firma del participante. Si está vacía/undefined se renderiza un recuadro "FIRMA: ___" — útil para preview previo a firma. */
  participantSignatureDataUrl?: string;
}): Promise<Blob> {
  const { snapshot, participantSignatureDataUrl } = args;
  const pdf = new jsPDF({ unit: "mm", format: "letter" });
  const ctx: PdfContext = { pdf, y: PAGE_MARGIN };

  writeTitle(
    ctx,
    "CONSENTIMIENTO INFORMADO, AUTORIZACIONES Y RESPONSABILIDAD - PARTICIPANTE MAYOR DE EDAD",
  );

  // 1. Identificación
  writeSection(ctx, "1. IDENTIFICACIÓN DEL PARTICIPANTE");
  writeParagraph(
    ctx,
    `Yo, ${snapshot.memberFullName}, mayor de edad, identificado(a) con ${snapshot.memberIdDocumentType} No. ${snapshot.memberIdDocumentNumber}${snapshot.memberIdExpeditionCity ? ` expedida en ${snapshot.memberIdExpeditionCity}` : ""}${snapshot.memberAddress ? `, con domicilio en ${snapshot.memberAddress}` : ""}, actuando en nombre propio y en pleno uso de mis facultades legales, MANIFIESTO que he sido debidamente informado(a) y otorgo mi consentimiento libre, previo, expreso e informado.`,
  );

  // 2. Participación
  writeSection(ctx, "2. PARTICIPACIÓN EN LA ACTIVIDAD");
  writeKeyValue(ctx, "Actividad", snapshot.eventName);
  writeKeyValue(
    ctx,
    "Lugar",
    `${snapshot.eventLocationName} - ${snapshot.eventLocationCity}`,
  );
  writeKeyValue(
    ctx,
    "Fechas",
    `Del ${formatDateEs(snapshot.eventStartDate)} al ${formatDateEs(snapshot.eventEndDate)}`,
  );
  if (snapshot.departureCity)
    writeKeyValue(ctx, "Ciudad de salida", snapshot.departureCity);
  if (snapshot.returnCity)
    writeKeyValue(ctx, "Ciudad de regreso", snapshot.returnCity);
  writeKeyValue(
    ctx,
    "Entidad organizadora",
    "Iglesia Adventista del Séptimo Día",
  );
  writeKeyValue(ctx, "Club / Dependencia", snapshot.clubName);
  if (snapshot.districtName)
    writeKeyValue(ctx, "Distrito", snapshot.districtName);

  // 3. Médica
  writeSection(ctx, "3. INFORMACIÓN MÉDICA Y CONDICIONES DE SALUD");
  if (snapshot.memberHealthInsurance) {
    writeKeyValue(ctx, "EPS", snapshot.memberHealthInsurance);
  }
  if (snapshot.memberNoMedicalConditions) {
    writeParagraph(
      ctx,
      "Declaro bajo gravedad de juramento que no presento condiciones médicas relevantes conocidas que impidan mi participación.",
    );
  } else {
    if (snapshot.memberMedicalConditions) {
      writeKeyValue(
        ctx,
        "Condiciones médicas",
        snapshot.memberMedicalConditions,
      );
    }
    if (snapshot.memberAllergies) {
      writeKeyValue(ctx, "Alergias", snapshot.memberAllergies);
    }
    if (snapshot.memberMedications) {
      writeKeyValue(
        ctx,
        "Medicamentos permanentes",
        snapshot.memberMedications,
      );
    }
    if (
      !snapshot.memberMedicalConditions &&
      !snapshot.memberAllergies &&
      !snapshot.memberMedications
    ) {
      writeParagraph(
        ctx,
        "No se registró información médica relevante adicional.",
      );
    }
  }

  // 4. Atención médica
  writeSection(ctx, "4. AUTORIZACIÓN PARA ATENCIÓN MÉDICA DE URGENCIA");
  writeParagraph(
    ctx,
    "Autorizo a los organizadores y responsables de la actividad para que, en caso de emergencia o urgencia médica, se gestione y autorice la atención médica necesaria, incluyendo traslado a centros asistenciales. Los gastos derivados de dicha atención serán asumidos conforme a la cobertura de mi EPS, póliza o seguro correspondiente.",
  );

  // 5. Exoneración
  writeSection(ctx, "5. EXONERACIÓN DE RESPONSABILIDAD");
  writeParagraph(
    ctx,
    "Declaro que participo de manera voluntaria y consciente, asumiendo los riesgos normales derivados de la actividad, y exonero a la Iglesia Adventista del Séptimo Día, a sus directivos, organizadores, coordinadores y voluntarios, de responsabilidad civil por hechos derivados de mi conducta personal, negligencia o incumplimiento de las normas establecidas, salvo dolo o culpa grave conforme a la ley.",
  );

  // 6. Imagen
  writeSection(ctx, "6. AUTORIZACIÓN DE USO DE IMAGEN");
  writeParagraph(
    ctx,
    `Autorizo a la Unión Colombiana del Norte, ${snapshot.associationName} y al Club de Conquistadores ${snapshot.clubName} (IASD), para el uso de mi imagen, voz y nombre, captados en fotografías, videos y producciones audiovisuales realizadas durante la actividad, con fines institucionales, educativos, informativos y pastorales, sin ánimo de lucro. Esta autorización se concede sin límite territorial ni temporal, conforme a la Ley 1581 de 2012, pudiendo ser revocada mediante solicitud escrita.`,
  );

  // 7. Declaraciones
  writeSection(ctx, "7. DECLARACIONES FINALES");
  writeParagraph(
    ctx,
    "Declaro que: (a) he leído y comprendido el contenido del presente documento; (b) la información suministrada es veraz; (c) firmo de manera libre, voluntaria y sin ningún tipo de presión.",
  );

  // 8. Firma
  writeSection(ctx, "8. FIRMA");
  ensureSpace(ctx, 50);
  if (participantSignatureDataUrl) {
    try {
      pdf.addImage(
        participantSignatureDataUrl,
        getJsPdfImageFormat(participantSignatureDataUrl),
        PAGE_MARGIN,
        ctx.y,
        60,
        25,
      );
    } catch (e) {
      console.warn("addImage signature failed", e);
    }
  } else {
    pdf.setDrawColor(180);
    pdf.rect(PAGE_MARGIN, ctx.y, 60, 25);
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text("(pendiente de firma)", PAGE_MARGIN + 30, ctx.y + 13, {
      align: "center",
    });
    pdf.setTextColor(0);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
  }
  ctx.y += 30;
  writeKeyValue(ctx, "Nombre completo", snapshot.memberFullName);
  writeKeyValue(
    ctx,
    "Documento",
    `${snapshot.memberIdDocumentType} ${snapshot.memberIdDocumentNumber}`,
  );
  writeKeyValue(ctx, "Fecha de firma", new Date().toLocaleDateString("es-CO"));

  return pdf.output("blob");
}
